import { useTranslation } from "react-i18next"

import type { LocalAiLeadCostBreakdownResponse } from "@/api/local-ai-lead-api"

import { Calculator, Info } from "@/components/icons"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { DashboardSectionCard } from "./admin-dashboard-shared"

type CostEstimateCardProps = {
  data: LocalAiLeadCostBreakdownResponse | null
  isLoading?: boolean
  formatCurrency: (value: number) => string
  /** When set, the tile also shows the allocated shared amount (monthly). */
  allocatedSharedMonthly?: number | null
}

function StatBlock({
  label,
  value,
  isLoading,
}: {
  label: string
  value: string
  isLoading?: boolean
}) {
  return (
    <div className="bg-muted/40 flex-1 rounded-xl p-4">
      <div className="text-3xl leading-tight font-semibold">
        {isLoading ? <span className="opacity-50">—</span> : value}
      </div>
      <div className="text-muted-foreground mt-1 text-sm">{label}</div>
    </div>
  )
}

// Format a count defensively — a missing/non-finite value renders as "—"
// rather than throwing (the cost-breakdown response may omit fields when an
// older backend is in play).
function fmtCount(n: number | null | undefined): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("sv-SE") : "—"
}

function BreakdownRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  )
}

export function CostEstimateCard({
  data,
  isLoading,
  formatCurrency,
  allocatedSharedMonthly,
}: CostEstimateCardProps) {
  const { t } = useTranslation()
  const isAnnualModel = data?.costModel === "shared_annual_v1"

  return (
    <DashboardSectionCard
      icon={Calculator}
      id="local-ai-lead-cost-estimate"
      description={t(
        isAnnualModel
          ? "localAiLeadPage.costEstimate.companyDescription"
          : "localAiLeadPage.costEstimate.description",
        isAnnualModel
          ? "Estimated monthly cost for this company, including its share of central services"
          : "Estimated monthly cost for the users assigned to you"
      )}
      title={
        isAnnualModel
          ? t("localAiLeadPage.costEstimate.companyTitle", "Your company's monthly cost")
          : t("localAiLeadPage.costEstimate.title", "Monthly Cost Estimate")
      }
    >
      {/* Headline figures */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <StatBlock
          isLoading={isLoading}
          label={t("localAiLeadPage.costEstimate.perUser", "Monthly cost per user")}
          value={
            typeof data?.monthlyCostPerUser === "number"
              ? formatCurrency(data.monthlyCostPerUser)
              : "—"
          }
        />
        <StatBlock
          isLoading={isLoading}
          label={t("localAiLeadPage.costEstimate.total", "Monthly cost total")}
          value={
            typeof data?.monthlyCostTotal === "number" ? formatCurrency(data.monthlyCostTotal) : "—"
          }
        />
      </div>

      {/* Global active-user count is shown; the platform cost amount is
          intentionally not exposed to the lead (see issue #2057). */}
      {data ? (
        <div className="space-y-1">
          <BreakdownRow
            label={t("localAiLeadPage.costEstimate.activeUsersGlobal", "Active users globally")}
            value={fmtCount(data.globalActiveUsers)}
          />
          <BreakdownRow
            value={fmtCount(data.assignedActiveUsers)}
            label={
              isAnnualModel
                ? t("localAiLeadPage.costEstimate.companyUsers", "Active users at this company")
                : t("localAiLeadPage.costEstimate.assignedUsers", "Your assigned active users")
            }
          />
          <BreakdownRow
            label={
              isAnnualModel
                ? t("localAiLeadPage.costEstimate.companyUsage", "Company usage")
                : t("localAiLeadPage.costEstimate.directUsage", "Your users' direct usage")
            }
            value={
              typeof data.leadDirectUsageMonthly === "number"
                ? formatCurrency(data.leadDirectUsageMonthly)
                : "—"
            }
          />
          {typeof allocatedSharedMonthly === "number" ? (
            <BreakdownRow
              label={t("localAiLeadPage.costEstimate.shared", "Allocated shared cost")}
              value={formatCurrency(allocatedSharedMonthly)}
            />
          ) : null}
        </div>
      ) : null}

      {/* Footnote */}
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="inline-flex items-center gap-1.5">
              <Info className="size-3.5" />
              {t(
                "localAiLeadPage.costEstimate.footnote",
                "Figures are normalized to a 30-day month."
              )}
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t(
                "localAiLeadPage.costEstimate.formula",
                "Estimated monthly cost for your assigned users — their share of running the platform (split across all active users globally), a fixed per-user infrastructure allowance, plus their own usage. Use the 30-day period for the truest monthly figure."
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </DashboardSectionCard>
  )
}
